import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

/**
 * Count the active invocations associated with a dataset.
 *
 * Polls every 5 seconds by default; pass `refetchInterval: false` to fetch once.
 */
export function useActiveInvocationCount(
  datasetId: string | undefined,
  options?: { refetchInterval?: number | false },
) {
  const { actionService } = useDomainServices();
  const { currentOrganization } = useAuth();

  return useQuery({
    queryKey: ["activeInvocationCount", datasetId, currentOrganization?.org_id],
    queryFn: async ({ signal }) => {
      if (currentOrganization === null) {
        throw new Error("Requires login");
      }

      if (datasetId === undefined) {
        throw new Error("Requires datasetId");
      }

      return actionService.countActiveInvocationsForDataset(
        currentOrganization.org_id,
        datasetId,
        { abortSignal: signal },
      );
    },
    enabled: currentOrganization !== null && datasetId !== undefined,
    refetchInterval: options?.refetchInterval ?? 5_000, // ms
    // Consider data stale immediately so it refetches on mount
    staleTime: 0,
  });
}
